import { type CSSProperties, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Outlet, useLocation } from 'react-router'

import { ProductLoadingPage, useProductShell } from '@/components/sidebar'
import { PageSpinner } from '@/components/ui/PageSpinner'
import { SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { AgentKind, AgentKindProvider } from '../../../components/AgentChat/AgentKindProvider'
import { AppLoadError } from '../../../components/AppLoadError'
import { AskCaiDrawer } from '../../../components/AskCai/AskCaiDrawer'
import { AskCaiProvider } from '../../../components/AskCai/AskCaiProvider'
import { Container } from '../../../components/Container'
import { MaintenanceBanner } from '../../../components/MaintenanceBanner'
import { OnboardingBanner } from '../../../components/OnboardingBanner'
import { OnboardingBannerContext, useOnboardingBannerClaims } from '../../../components/OnboardingBannerSlot'
import {
  onboardingStage,
  showsOnboardingBanner,
  useCurrentUser,
  worksFromQuotesPage,
} from '../../../models/user'
import { APP_TOP_BAR_HEIGHT, AppNavigation, AppShellTopBarSlots } from './AppNavigation'
import { AppSidebar } from './AppSidebar'
import { ActiveSidebarTabProvider } from './sidebar/ActiveSidebarTab'
import { TabPage } from './TabPage'

export function AppLayout() {
  // Default to expanded on first load (no stored preference); honor a saved collapse.
  const [open, setOpen] = useState(window.localStorage.getItem('sidebar-open') !== 'false')
  const [dismissedBanner, setDismissedBanner] = useState<string | null>(null)
  const { user, isLoading, isError, error, isRefetching, refetch } = useCurrentUser({ enabled: true })
  const shell = useProductShell()
  const { claim: claimOnboardingBanner, claimed: onboardingBannerPlaced } = useOnboardingBannerClaims()
  const location = useLocation()
  const isInternalPage = location.pathname.includes('/internal')
  // The vault is its own errand; an insurance nudge on top of it competes with the ask.
  const isVaultPage = location.pathname.startsWith('/insure/vault')
  // Settings is account housekeeping, not a place to be sent back to the application.
  const isSettingsPage = location.pathname.startsWith('/insure/admin-settings')
  // Master/detail split views and wide tables cap their own content width;
  // everything else keeps the max-w-7xl cap, left-aligned on internal pages.
  const isFullWidthPage =
    location.pathname.includes('/internal/sales-leads') ||
    location.pathname.includes('/internal/agent-chat') ||
    location.pathname.includes('/internal/emails') ||
    location.pathname.includes('/internal/customers') ||
    location.pathname.includes('/internal/qa/geico') ||
    location.pathname.includes('/internal/qa/emails')

  // A page scrollbar that comes and goes with page height shifts every page sideways between tabs.
  useEffect(() => {
    document.documentElement.classList.add('no-scrollbar')
    return () => document.documentElement.classList.remove('no-scrollbar')
  }, [])

  // Only a failed cold start errors; a cached user survives failed refetches.
  if (isError && !user) {
    return <AppLoadError error={error} onRetry={() => void refetch()} retrying={isRefetching} />
  }

  if (isLoading || !user) {
    return shell ? <ProductLoadingPage /> : <PageSpinner />
  }

  const banner = user.maintenanceBanner
  // Key the dismissal to the notice so a reworded or escalated one shows again.
  const bannerKey = banner ? `${banner.state}:${banner.message}` : null

  const onboardingBanner =
    showsOnboardingBanner(user) && !isInternalPage && !isVaultPage && !isSettingsPage ? (
      <OnboardingBanner
        stage={onboardingStage(user.currentOrg, user.currentOrg.currentInsuranceApplication)}
      />
    ) : null

  // Unmounted rather than left closed: the panel reopens itself from sessionStorage.
  // Beside Cai's docked chat, Insure's own assistant steps aside, its composers along with it.
  const showsAskCaiDrawer = !isInternalPage && !worksFromQuotesPage(user.currentOrg) && !shell?.caiDocked

  function handleOpenChange(open: boolean) {
    setOpen(open)
    window.localStorage.setItem('sidebar-open', open.toString())
  }

  return (
    // Above the Outlet so the panel and its streaming turn survive navigation.
    <AskCaiProvider>
      <ActiveSidebarTabProvider>
        {/* shrink-0: #root is a full-height flex column, so without it a tall page squeezes to one screen. */}
        <SidebarProvider
          open={open}
          onOpenChange={handleOpenChange}
          className="shrink-0 flex-col"
          style={
            (shell
              ? // The shared shell's panel scrolls under its bar, so sticky parts pin to the panel's top.
                { '--app-top-bar-height': '0px', '--app-rail-height': 'var(--product-panel-height)' }
              : {
                  '--app-top-bar-height': APP_TOP_BAR_HEIGHT,
                  '--app-rail-height': `calc(100svh - ${APP_TOP_BAR_HEIGHT})`,
                }) as CSSProperties
          }
        >
          <Helmet>
            <title>App</title>
          </Helmet>
          {banner && bannerKey !== dismissedBanner && (
            <MaintenanceBanner
              state={banner.state}
              message={banner.message}
              onDismiss={() => setDismissedBanner(bannerKey)}
            />
          )}
          {shell ? <AppShellTopBarSlots /> : <AppNavigation />}
          <div className="flex flex-1">
            <AppSidebar />
            {/* Unpadded sideways so the Containers below share one px-4 gutter; min-w-0 keeps a wide child scrolling in its own container, not the page. */}
            <main className="flex min-w-0 flex-1 flex-col pt-6">
              <TabPage>
                {/* Pages put it under their title; one that places no slot still gets it on top. */}
                {onboardingBanner && !onboardingBannerPlaced && (
                  <Container className="pb-6">{onboardingBanner}</Container>
                )}
                <div className="flex flex-1 flex-col gap-4 pb-6">
                  <Container
                    className={cn(
                      'flex flex-1 flex-col',
                      isInternalPage && 'mx-0',
                      isFullWidthPage && 'max-w-none'
                    )}
                  >
                    <OnboardingBannerContext.Provider
                      value={{ banner: onboardingBanner, claim: claimOnboardingBanner }}
                    >
                      <Outlet />
                    </OnboardingBannerContext.Provider>
                  </Container>
                </div>
              </TabPage>
            </main>
          </div>
          {/* Staff on an internal page get the internal agent's own panel from
              InternalLayout. No floating launcher here — the panel opens from the
              composers that embed <AskCai />. */}
          {showsAskCaiDrawer && (
            <AgentKindProvider kind={AgentKind.ASK_CAI}>
              <AskCaiDrawer />
            </AgentKindProvider>
          )}
        </SidebarProvider>
      </ActiveSidebarTabProvider>
    </AskCaiProvider>
  )
}
